import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchRecentViews, removeView, subscribeToViewChanges } from '../services/views';
import { normalizeId } from '../utils/ids';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

// A little headroom over what the rail shows, so a row whose collection the
// server drops (un-shared, deleted) does not leave a gap.
const FETCH_LIMIT = 8;

/**
 * The collections this user opened most recently, newest first — the rail's
 * Recent section. Server-side history, so it follows the user between
 * devices, and it re-fetches whenever anything records, forgets, or clears a
 * view (services/views.js publishes those).
 *
 * Returns { rows, loading, forget } — `rows` is [{ id, collection }], and
 * `forget(id)` drops one row optimistically and tells the server.
 */
export default function useRecentCollections({ limit = 5 } = {}) {
	const [views, setViews] = useState(null);
	// A fetch in flight must not overwrite a newer one's result.
	const requestRef = useRef(0);
	// Keyed on WHO is signed in, not just whether: another account signing in
	// on this tab kept the last one's Recent rail (both are "signed in").
	const session = useOnSessionChange(() => {
		requestRef.current += 1;
		setViews(null);
	});

	const load = useCallback(() => {
		// The session as it is NOW, not as this render saw it: /logout drops the
		// token in a layout effect, after the shell rendered signed in and
		// before this effect runs — and a tokenless read is a 401.
		if (!session || !getSessionUserId()) { setViews([]); return; }
		const token = ++requestRef.current;
		fetchRecentViews(FETCH_LIMIT, 'collection')
			.then(rows => { if (token === requestRef.current) setViews(rows); })
			// The rail works without its Recent section; a failure shows nothing.
			.catch(() => { if (token === requestRef.current) setViews([]); });
	}, [session]);

	useEffect(load, [load]);
	// Only a change to COLLECTION history can change this list. Every item
	// the items page opens is recorded as a view; refetching the rail for
	// each of those asked the server a question whose answer had not moved.
	useEffect(() => subscribeToViewChanges(change => {
		if (!change?.targetType || change.targetType === 'collection') load();
	}), [load]);

	const forget = useCallback(async (id) => {
		const target = normalizeId(id);
		if (!target) return;
		// Optimistic: the row goes now, and the service's change notification
		// re-syncs everything else that shows history.
		setViews(current => (current || []).filter(view => normalizeId(view.target_id) !== target));
		try {
			await removeView('collection', target);
		} catch {
			load(); // put it back if the server disagreed
		}
	}, [load]);

	const rows = (views || [])
		// The server narrows by type; the check is repeated here so a row of
		// another kind can never be drawn as a collection.
		.filter(view => view?.targetType === 'collection')
		.map(view => ({ id: normalizeId(view?.target_id), collection: view?.target }))
		.filter(row => row.id && row.collection)
		.slice(0, limit);

	return { rows, loading: views === null, forget };
}
